import React from 'react';
import { toast } from 'sonner';

import {
  type FirmDisplayService,
  useFirmServicesWithCatalogue,
} from '@/components/services';
import { Badge } from '@/components/ui/catalyst/badge';
import { Skeleton } from '@/components/ui/skeleton';
import type { FirmServiceWithWorkflowsSchema } from '@/services/api/generated/firmsService';
import { getApiErrorMessage } from '@/utils/api-error';

interface ServicesListProps {
  firmId: string;
  selectedServiceId: string | null;
  onSelectService: (service: FirmServiceWithWorkflowsSchema) => void;
}

export const ServicesList: React.FC<ServicesListProps> = ({
  firmId,
  selectedServiceId,
  onSelectService,
}) => {
  const { services, isLoading, createFirmService, isCreating } =
    useFirmServicesWithCatalogue(firmId);

  if (isLoading) {
    return (
      <div className="space-y-1.5">
        <Skeleton className="h-12 w-full rounded-lg" />
        <Skeleton className="h-12 w-full rounded-lg" />
        <Skeleton className="h-12 w-full rounded-lg" />
      </div>
    );
  }

  const handleServiceClick = async (displayService: FirmDisplayService) => {
    if (!displayService.isFirmService) {
      // Auto-create FirmService on first click
      try {
        const created = await createFirmService({
          firmId,
          createFirmServiceSchema: { service_id: displayService.service.id },
        }).unwrap();

        // Created service starts with empty workflows
        const firmService: FirmServiceWithWorkflowsSchema = {
          ...created,
          workflows: [],
        };
        onSelectService(firmService);
      } catch (error) {
        toast.error(
          `Failed to configure service: ${getApiErrorMessage(error)}`,
        );
      }
    } else {
      // Already a FirmService - construct from display data
      const firmService: FirmServiceWithWorkflowsSchema = {
        id: displayService.id,
        firm_id: firmId,
        service_id: displayService.service.id,
        service: displayService.service,
        notes: '',
        config: null,
        is_active: displayService.is_active,
        workflows: displayService.firmWorkflows ?? [],
      };
      onSelectService(firmService);
    }
  };

  return (
    <div className="space-y-1.5">
      {services.map((displayService) => (
        <ServiceListItem
          key={displayService.service.id}
          displayService={displayService}
          isSelected={selectedServiceId === displayService.id}
          isDisabled={isCreating}
          onClick={() => handleServiceClick(displayService)}
        />
      ))}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Sub-component: ServiceListItem
// -----------------------------------------------------------------------------

interface ServiceListItemProps {
  displayService: FirmDisplayService;
  isSelected: boolean;
  isDisabled: boolean;
  onClick: () => void;
}

const ServiceListItem: React.FC<ServiceListItemProps> = ({
  displayService,
  isSelected,
  isDisabled,
  onClick,
}) => {
  const workflowsEnabled =
    displayService.workflows?.filter((w) => w.is_enabled).length ?? 0;
  const totalWorkflows = displayService.workflows?.length ?? 0;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isDisabled}
      className={getItemClassName(isSelected, displayService.is_active)}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <span className="font-medium text-xs text-zinc-950 dark:text-white truncate">
            {displayService.service.display_name}
          </span>
          {displayService.isFirmService && totalWorkflows > 0 && (
            <span
              className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-medium rounded-full bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
              title={`${workflowsEnabled} of ${totalWorkflows} workflows enabled`}
            >
              {workflowsEnabled}/{totalWorkflows}
            </span>
          )}
        </div>
        <Badge
          color={displayService.is_active ? 'blue' : 'zinc'}
          className="flex-shrink-0"
        >
          {displayService.is_active ? 'On' : 'Off'}
        </Badge>
      </div>
    </button>
  );
};

// -----------------------------------------------------------------------------
// Style helper
// -----------------------------------------------------------------------------

const getItemClassName = (isSelected: boolean, isActive: boolean): string => {
  const base =
    'w-full text-left px-2.5 py-2 rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-wait';

  if (isSelected) {
    return `${base} bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800`;
  }

  if (isActive) {
    return `${base} bg-white border-zinc-200 hover:bg-zinc-50 dark:bg-zinc-900 dark:border-zinc-800 dark:hover:bg-zinc-800`;
  }

  return `${base} bg-zinc-50 border-zinc-200 opacity-60 dark:bg-zinc-900 dark:border-zinc-800`;
};
